import { useEffect, useCallback } from "react";
import _ from "lodash";
import { getOrganizationId } from "modules/CommonSelectors";
import useVirtualizedBatchFetcher from "./useVirtualizedBatchFetcher";
import useBatchSearch from "./useBatchSearch";
import {
  getGradeDetailsBatchQuery,
  searchGradesByIdsQuery,
} from "../modules/ParticipantsQueries";

const useGradeBatchDetails = ({ gradeIds, searchText, skip = false }) => {
  const organizationId = getOrganizationId();

  // ---------------- BATCH ----------------
  const {
    onRangeChanged,
    detailsMap,
    setDetailsMap,
  } = useVirtualizedBatchFetcher({
    ids: gradeIds,
    skip: skip || !!searchText,

    batchQuery: getGradeDetailsBatchQuery,

    batchVariablesBuilder: ids => ({
      ids,
    }),

    mapBatchData: data => {
      const items = _.get(data, "multiNode", []);
      return _.map(items, item => ({
        id: _.get(item, "id"),
        name: _.get(item, "name"),
        staffCount: _.get(item, "staffCount"),
        studentCount: _.get(item, "studentCount"),
        familyMemberCount: _.get(item, "familyMemberCount"),
        curriculumProgram: _.get(item, "curriculumProgram"),
      }));
    },
  });

  // ---------------- SEARCH ----------------

  const searchVariablesBuilder = useCallback(
    (searchText, ids) => ({
      organizationId,
      ids,
      searchText,
    }),
    [organizationId]
  );
  const { searchItems, searchIds, isSearchLoading } = useBatchSearch({
    ids: gradeIds,
    searchText,
    skip,

    searchQuery: searchGradesByIdsQuery,

    searchVariablesBuilder,

    mapSearchData: data => {
      const grades = _.get(data, "node.grades", []);
      return _.map(grades, item => ({
        id: _.get(item, "id"),
        name: _.get(item, "name"),
        staffCount: _.get(item, "staffCount"),
        studentCount: _.get(item, "studentCount"),
        familyMemberCount: _.get(item, "familyMemberCount"),
        curriculumProgram: _.get(item, "curriculumProgram"),
      }));
    },

    extractSearchIds: data => _.map(_.get(data, "node.grades", []), "id"),
  });

  // Merge search into detailsMap
  useEffect(() => {
    if (!searchItems) return;

    setDetailsMap(prev => {
      const next = { ...prev };
      _.forEach(searchItems, item => {
        if (_.get(item, "id")) next[item.id] = item;
      });
      return next;
    });
  }, [searchItems, setDetailsMap]);

  return {
    onRangeChanged,
    detailsMap,
    searchResults: searchText ? searchIds : null,
    isSearchLoading,
  };
};

export default useGradeBatchDetails;
